import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Chip,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import moment from "moment";

/**
 * TicketSelectionView Component
 * Shows patient summary when a ticket is selected
 * Includes button to view full patient history
 * 
 * Usage:
 * <TicketSelectionView ticket={selectedTicket} />
 */
export default function TicketSelectionView({ ticket }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!ticket) {
    return (
      <Paper sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          {t("reception.noTicketSelected")}
        </Typography>
      </Paper>
    );
  }

  const handleViewPatientHistory = () => {
    // Navigate to ticket profile (patient profile in context of this ticket)
    navigate(`/patient-profile/ticket/${ticket.id}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1: // Active
        return "success";
      case 2: // Completed
        return "info";
      case 3: // Cancelled
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          {t("reception.ticketInformation")}
        </Typography>
        <Chip
          label={ticket.status_display}
          color={getStatusColor(ticket.status)}
          size="small"
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={2}>
        {/* Ticket Number */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t("reception.visitTicketNumber", { defaultValue: "Visit Ticket Number" })}
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary">
                {ticket.ticket_number}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Patient Information */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("reception.patientInformation")}
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("reception.name")}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {ticket.patient_name_display || "N/A"}
                  </Typography>
                </Grid>

                {ticket.patient_mrn_display && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t("reception.mrn", { defaultValue: "MRN" })}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {ticket.patient_mrn_display}
                    </Typography>
                  </Grid>
                )}

                {ticket.patient_father_name_display || ticket.patient_father_name ? (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t("reception.fathersName")}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {ticket.patient_father_name_display || ticket.patient_father_name}
                    </Typography>
                  </Grid>
                ) : null}

                {(ticket.patient_phone_display || ticket.patient_phone) && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <PhoneIcon sx={{ mr: 1, fontSize: 18, color: "text.secondary" }} />
                      <Typography variant="body1">
                        {ticket.patient_phone_display || ticket.patient_phone}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {ticket.patient_age_display && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t("reception.age")}
                    </Typography>
                    <Typography variant="body1">
                      {ticket.patient_age_display} years
                    </Typography>
                  </Grid>
                )}

                {ticket.patient_gender_display && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t("reception.gender")}
                    </Typography>
                    <Typography variant="body1">
                      {ticket.patient_gender_display}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Visit Information */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <CalendarIcon sx={{ mr: 1, fontSize: 18, color: "text.secondary" }} />
                <Typography variant="subtitle2" color="text.secondary">
                  {t("reception.visitDate")}
                </Typography>
              </Box>
              <Typography variant="body1">
                {moment(ticket.visit_date).format("YYYY-MM-DD HH:mm")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {ticket.department_name_display && (
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t("reception.department")}
                </Typography>
                <Typography variant="body1">
                  {ticket.department_name_display}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Encounters Count */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t("reception.encounters")}
              </Typography>
              <Typography variant="h6" color="primary">
                {ticket.encounters_count || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* View Full History Button */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<ViewIcon />}
          onClick={handleViewPatientHistory}
          sx={{ minWidth: 200 }}
        >
          {t("reception.viewFullPatientHistory")}
        </Button>
      </Box>
    </Paper>
  );
}
